/**
 * useSpineCanvas
 *
 * Renders Spine animations directly using a PixiJS Application.
 * App is created once when playing starts; animation changes swap in-place.
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import { Application } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { SpineAsset } from '../../core/types/spine.types';
import { spineOffscreenRenderer } from '../services/SpineOffscreenRenderer';

// Global sync: tracks when each animation started so remounted instances can seek to the right time
const animationStartTimes = new Map<string, number>(); // syncKey → performance.now()
const animationRefCounts = new Map<string, number>(); // syncKey → number of active instances

function registerAnimInstance(syncKey: string): void {
  animationRefCounts.set(syncKey, (animationRefCounts.get(syncKey) || 0) + 1);
}

function unregisterAnimInstance(syncKey: string): void {
  const count = (animationRefCounts.get(syncKey) || 1) - 1;
  if (count <= 0) {
    animationRefCounts.delete(syncKey);
    animationStartTimes.delete(syncKey);
  } else {
    animationRefCounts.set(syncKey, count);
  }
}

interface UseSpineCanvasOptions {
  spineAsset: SpineAsset | null;
  skinName: string;
  animationName: string | null;
  isPlaying: boolean;
  loop: boolean;
  timeScale: number;
  mixDuration: number;
}

interface UseSpineCanvasResult {
  containerRef: (node: HTMLDivElement | null) => void;
  active: boolean;
}

export function useSpineCanvas(options: UseSpineCanvasOptions): UseSpineCanvasResult {
  const { spineAsset, skinName, animationName, isPlaying, loop, timeScale, mixDuration } = options;

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);
  const spineRef = useRef<Spine | null>(null);
  const [active, setActive] = useState(false);
  const currentAssetId = useRef<string | null>(null);
  const boundsRef = useRef<{ cx: number; cy: number; bw: number; bh: number } | null>(null);
  const currentSyncKey = useRef<string | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node);
  }, []);

  const destroyAll = useCallback(() => {
    if (currentSyncKey.current) {
      unregisterAnimInstance(currentSyncKey.current);
      currentSyncKey.current = null;
    }
    if (spineRef.current) { spineRef.current.destroy(); spineRef.current = null; }
    if (appRef.current) {
      const c = appRef.current.canvas as HTMLCanvasElement;
      if (c) c.style.visibility = 'hidden';
      appRef.current.destroy(true);
      appRef.current = null;
    }
    currentAssetId.current = null;
    boundsRef.current = null;
    setActive(false);
  }, []);

  // Effect 1: Create/destroy the PixiJS app + Spine instance when play state changes
  // Does NOT depend on animationName — animation swaps are handled by Effect 2
  useEffect(() => {
    if (!containerEl || !spineAsset || !isPlaying) {
      destroyAll();
      return;
    }

    // Already set up for this asset? Skip — Effect 2 handles animation changes
    if (appRef.current && currentAssetId.current === spineAsset.id) {
      return;
    }

    let cancelled = false;

    const setup = async () => {
      try {
        const skeletonData = await spineOffscreenRenderer.getSkeletonData(
          spineAsset.skeletonPath,
          spineAsset.atlasPath,
          spineAsset.texturePaths
        );
        if (cancelled) return;

        destroyAll();

        const spine = new Spine(skeletonData);
        if (skinName && skinName !== 'default') {
          try { spine.skeleton.setSkinByName(skinName); spine.skeleton.setSlotsToSetupPose(); }
          catch { /* default */ }
        }
        spine.state.data.defaultMix = mixDuration;

        // Set initial animation and sync to existing playback if another view is already playing
        if (animationName) {
          try { spine.state.setAnimation(0, animationName, loop); } catch { /* skip */ }
          const syncKey = `${spineAsset.skeletonPath}:${animationName}`;
          const startedAt = animationStartTimes.get(syncKey);
          if (startedAt) {
            // Another instance is already playing — seek to current position
            const elapsed = (performance.now() - startedAt) / 1000 * timeScale;
            spine.update(elapsed);
          } else {
            // First instance — record start time
            animationStartTimes.set(syncKey, performance.now());
            spine.update(0);
          }
          registerAnimInstance(syncKey);
          currentSyncKey.current = syncKey;
        } else {
          spine.update(0);
        }

        const rect = containerEl.getBoundingClientRect();
        const containerW = Math.max(1, rect.width);
        const containerH = Math.max(1, rect.height);

        const app = new Application();
        await app.init({
          backgroundAlpha: 0,
          antialias: true,
          width: Math.round(containerW),
          height: Math.round(containerH),
        });
        if (cancelled) { app.destroy(true); spine.destroy(); return; }

        const canvas = app.canvas as HTMLCanvasElement;
        canvas.style.cssText = 'width:100%;height:100%;display:block;visibility:hidden;';
        containerEl.appendChild(canvas);
        app.stage.addChild(spine);

        // Measure unscaled bounds and store for resize handler
        app.renderer.render(app.stage);
        const bounds = spine.getBounds();
        const bw = bounds.maxX - bounds.minX;
        const bh = bounds.maxY - bounds.minY;
        const boundsOriginX = (bounds.minX + bounds.maxX) / 2;
        const boundsOriginY = (bounds.minY + bounds.maxY) / 2;

        // Store for resize handler — these are the unscaled bounds at spine origin
        boundsRef.current = { cx: boundsOriginX, cy: boundsOriginY, bw, bh };

        // Fit spine to container
        if (bw > 0 && bh > 0) {
          const fitScale = Math.min(containerW / bw, containerH / bh) * 0.95;
          spine.scale.set(fitScale);
          spine.x = containerW / 2 - boundsOriginX * fitScale;
          spine.y = containerH / 2 - boundsOriginY * fitScale;
        } else {
          spine.x = containerW / 2;
          spine.y = containerH / 2;
        }

        if (cancelled) { spine.destroy(); app.destroy(true); return; }

        app.renderer.render(app.stage);
        canvas.style.visibility = 'visible';

        appRef.current = app;
        spineRef.current = spine;
        currentAssetId.current = spineAsset.id;
        spine.autoUpdate = true;
        spine.state.timeScale = timeScale;
        setActive(true);
      } catch (err) {
        console.error('[SpineCanvas] setup failed:', err);
      }
    };

    setup();

    return () => {
      cancelled = true;
      destroyAll();
    };
  }, [containerEl, spineAsset?.id, isPlaying, skinName]);

  // Effect 2: Resize renderer + reposition spine when container size changes
  useEffect(() => {
    if (!containerEl || !appRef.current || !spineRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const app = appRef.current;
      const spine = spineRef.current;
      const b = boundsRef.current;
      if (!app || !spine || !b || b.bw <= 0 || b.bh <= 0) return;

      const entry = entries[0];
      if (!entry) return;

      const newW = Math.max(1, Math.round(entry.contentRect.width));
      const newH = Math.max(1, Math.round(entry.contentRect.height));

      // Resize renderer to new container size
      app.renderer.resize(newW, newH);
      const canvas = app.canvas as HTMLCanvasElement;
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      // Refit spine using stored unscaled bounds — no re-measurement needed
      const fitScale = Math.min(newW / b.bw, newH / b.bh) * 0.95;
      spine.scale.set(fitScale);
      spine.x = newW / 2 - b.cx * fitScale;
      spine.y = newH / 2 - b.cy * fitScale;
    });

    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [containerEl, active]);

  // Effect 3: Swap animation in-place on existing Spine — no app recreation
  useEffect(() => {
    const spine = spineRef.current;
    if (!spine || !animationName || !spineAsset) return;

    try {
      spine.state.setAnimation(0, animationName, loop);
      // Unregister old, register new animation for sync
      if (currentSyncKey.current) unregisterAnimInstance(currentSyncKey.current);
      const syncKey = `${spineAsset.skeletonPath}:${animationName}`;
      animationStartTimes.set(syncKey, performance.now());
      registerAnimInstance(syncKey);
      currentSyncKey.current = syncKey;
    } catch {
      console.warn(`[SpineCanvas] Animation not found: ${animationName}`);
    }
  }, [animationName, loop]);

  // Live-update timeScale
  useEffect(() => {
    if (spineRef.current) spineRef.current.state.timeScale = timeScale;
  }, [timeScale]);

  // Live-update mixDuration
  useEffect(() => {
    if (spineRef.current) spineRef.current.state.data.defaultMix = mixDuration;
  }, [mixDuration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { destroyAll(); };
  }, [destroyAll]);

  return { containerRef, active };
}
